// Travel segment data for mainland China destinations
// Each segment shows km distance and driving hours between stops

export interface TravelSegment {
  km?: number;        // distance in km
  hours?: string;     // driving time, e.g. "3:30" or "29"
  isWalking?: boolean;
  walkTime?: string;  // e.g. "15分鐘"
  from?: string;      // departure point label
  to?: string;        // arrival point label
}

export interface DayTravelTimes {
  dayStart?: TravelSegment;   // travel from previous night's accommodation to day's first stop
  between: (TravelSegment | null)[];  // travel between activities[i] and activities[i+1]
}

export const travelTimesMap: Record<number, Record<number, DayTravelTimes>> = {

  // ─── 1. 川藏線 ─────────────────────────────────────────────
  1: {
    1: {
      between: [
        { from: "香港", to: "成都", km: 2100, hours: "22" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "成都", to: "折多山", km: 190, hours: "2:30" },
      between: [
        { from: "折多山", to: "康定", km: 25, hours: "0:30" },
        { from: "康定", to: "木格措", km: 28, hours: "0:35" },
      ],
    },
    3: {
      dayStart: { from: "康定", to: "塔公草原", km: 70, hours: "1:20" },
      between: [
        { from: "塔公草原", to: "新都橋", km: 80, hours: "1:30" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    4: {
      dayStart: { from: "新都橋", to: "高爾寺山", km: 100, hours: "2:00" },
      between: [
        { from: "高爾寺山", to: "理塘寺", km: 60, hours: "1:00" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    5: {
      dayStart: { from: "理塘", to: "鹽井鹽田", km: 280, hours: "4:00" },
      between: [
        { from: "鹽井", to: "如美溫泉", km: 15, hours: "0:20" },
        { from: "如美", to: "芒康縣城", km: 30, hours: "0:30" },
      ],
    },
    6: {
      dayStart: { from: "芒康", to: "業拉山72拐", km: 120, hours: "3:00" },
      between: [
        { from: "業拉山", to: "怒江大峽谷", km: 40, hours: "0:45" },
        { from: "怒江", to: "左貢", km: 50, hours: "1:00" },
      ],
    },
    7: {
      dayStart: { from: "左貢", to: "來古冰川", km: 210, hours: "4:30" },
      between: [
        { from: "來古冰川", to: "然烏湖", km: 5, hours: "0:15" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    8: {
      dayStart: { from: "然烏湖", to: "魯朗林海", km: 130, hours: "2:30" },
      between: [
        { from: "魯朗", to: "扎西崗村", km: 5, hours: "0:10" },
        { from: "扎西崗村", to: "南迦巴瓦觀景點", km: 40, hours: "0:45" },
      ],
    },
    9: {
      dayStart: { from: "林芝", to: "米拉山口", km: 170, hours: "3:30" },
      between: [
        { from: "米拉山口", to: "拉薩", km: 100, hours: "2:00" },
        { from: "拉薩市區", to: "八廓街", km: 3, hours: "0:20" },
      ],
    },
    10: {
      dayStart: { from: "酒店", to: "布達拉宮", isWalking: true, walkTime: "15分鐘" },
      between: [
        { isWalking: true, walkTime: "15分鐘" },
        { isWalking: true, walkTime: "20分鐘" },
      ],
    },
  },

  // ─── 2. 青海湖 ─────────────────────────────────────────────
  2: {
    1: {
      between: [
        { from: "香港", to: "西寧", km: 2672, hours: "29" },
        { from: "西寧市區", to: "東關清真大寺", km: 5, hours: "0:15" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "西寧", to: "茶卡鹽湖", km: 297, hours: "3:30" },
      between: [
        { isWalking: true, walkTime: "15分鐘" },
        { from: "茶卡鹽湖", to: "黑馬河", km: 90, hours: "1:30" },
      ],
    },
    3: {
      between: [
        { from: "黑馬河日出點", to: "環湖西路起點", km: 35, hours: "0:35" },
        { from: "環湖西路", to: "油菜花海觀景點", km: 45, hours: "0:45" },
      ],
    },
    4: {
      dayStart: { from: "海晏縣", to: "沙島", km: 60, hours: "0:50" },
      between: [
        { from: "沙島", to: "二郎劍景區", km: 30, hours: "0:30" },
        { from: "二郎劍", to: "環湖東路", km: 20, hours: "0:25" },
      ],
    },
    5: {
      dayStart: { from: "西寧酒店", to: "塔爾寺", km: 25, hours: "0:30" },
      between: [
        { from: "塔爾寺", to: "西寧市區手工藝品街", km: 25, hours: "0:30" },
        { from: "西寧", to: "香港", km: 2672, hours: "29" },
      ],
    },
  },

  // ─── 3. 獨庫公路 ─────────────────────────────────────────────
  3: {
    1: {
      between: [
        { from: "香港", to: "獨山子", km: 3800, hours: "42" },
        null,
      ],
    },
    2: {
      dayStart: { from: "獨山子", to: "天山北麓達坂", km: 130, hours: "2:30" },
      between: [
        { from: "達坂", to: "那拉提草原", km: 80, hours: "1:30" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    3: {
      dayStart: { from: "那拉提", to: "喬爾瑪烈士陵園", km: 100, hours: "1:30" },
      between: [
        { from: "喬爾瑪", to: "最美獨庫路段", km: 120, hours: "2:00" },
        { from: "獨庫公路", to: "庫車古城", km: 80, hours: "1:30" },
      ],
    },
    4: {
      dayStart: { from: "庫車酒店", to: "克孜爾石窟", km: 75, hours: "1:00" },
      between: [
        { from: "克孜爾石窟", to: "庫車王府", km: 75, hours: "1:00" },
        { from: "庫車王府", to: "美食街", km: 3, hours: "0:10" },
      ],
    },
    5: {
      dayStart: { from: "庫車", to: "溫宿大峽谷", km: 80, hours: "1:10" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    6: {
      dayStart: { from: "阿克蘇", to: "輪臺胡楊林", km: 150, hours: "2:00" },
      between: [
        { from: "胡楊林", to: "塔里木河", km: 5, hours: "0:10" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    7: {
      dayStart: { from: "輪臺", to: "庫爾勒香梨農場", km: 210, hours: "2:30" },
      between: [
        { from: "農場", to: "孔雀河景觀帶", km: 10, hours: "0:15" },
        { from: "孔雀河", to: "鐵門關", km: 12, hours: "0:15" },
      ],
    },
    8: {
      dayStart: { from: "庫爾勒", to: "烏魯木齊大巴扎", km: 470, hours: "4:30" },
      between: [
        { from: "大巴扎", to: "烏魯木齊機場/出發點", km: 5, hours: "0:15" },
        { from: "烏魯木齊", to: "香港", km: 3800, hours: "42" },
      ],
    },
  },

  // ─── 4. 海南島 ─────────────────────────────────────────────
  4: {
    1: {
      between: [
        { from: "香港", to: "海口", km: 600, hours: "6:30" },
        { from: "海口碼頭", to: "中山路騎樓街", km: 8, hours: "0:20" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    2: {
      dayStart: { from: "海口", to: "文昌航天城", km: 60, hours: "0:50" },
      between: [
        { from: "文昌", to: "椰林大道", km: 20, hours: "0:25" },
        { from: "椰林大道", to: "瓊海博鰲論壇會址", km: 80, hours: "1:00" },
      ],
    },
    3: {
      dayStart: { from: "瓊海", to: "日月灣", km: 50, hours: "0:45" },
      between: [
        { from: "日月灣", to: "石梅灣", km: 20, hours: "0:25" },
        { from: "石梅灣", to: "神州半島", km: 25, hours: "0:30" },
      ],
    },
    4: {
      dayStart: { from: "萬寧", to: "分界洲島碼頭", km: 45, hours: "0:40" },
      between: [
        { from: "分界洲島", to: "陵水新村漁港", km: 30, hours: "0:35" },
        { from: "陵水", to: "三亞", km: 80, hours: "1:00" },
      ],
    },
    5: {
      dayStart: { from: "三亞酒店", to: "亞龍灣", km: 18, hours: "0:25" },
      between: [
        { from: "亞龍灣", to: "天涯海角", km: 35, hours: "0:40" },
        { from: "天涯海角", to: "三亞夜市", km: 25, hours: "0:30" },
      ],
    },
    6: {
      dayStart: { from: "三亞", to: "五指山", km: 120, hours: "2:00" },
      between: [
        { from: "五指山雨林", to: "黎族村寨", km: 15, hours: "0:20" },
        { from: "黎族村寨", to: "海口返港", km: 180, hours: "2:30" },
      ],
    },
  },

  // ─── 5. 雲南環線 ─────────────────────────────────────────────
  5: {
    1: {
      between: [
        { from: "香港", to: "大理", km: 1800, hours: "20" },
        { from: "大理市區", to: "洱海", km: 8, hours: "0:20" },
      ],
    },
    2: {
      dayStart: { from: "大理酒店", to: "洱海碼頭", km: 5, hours: "0:15" },
      between: [
        { from: "洱海碼頭", to: "雙廊古鎮", km: 25, hours: "0:30" },
        { isWalking: true, walkTime: "30分鐘" },
      ],
    },
    3: {
      dayStart: { from: "大理", to: "沙溪古鎮", km: 100, hours: "1:30" },
      between: [
        { from: "沙溪", to: "麗江古城", km: 130, hours: "2:00" },
        { from: "麗江古城", to: "束河古鎮", km: 8, hours: "0:15" },
      ],
    },
    4: {
      dayStart: { from: "麗江", to: "玉龍雪山", km: 25, hours: "0:35" },
      between: [
        { from: "玉龍雪山", to: "藍月谷", km: 5, hours: "0:15" },
        { from: "藍月谷", to: "東巴文化演出場", km: 15, hours: "0:20" },
      ],
    },
    5: {
      dayStart: { from: "麗江", to: "虎跳峽入口", km: 60, hours: "1:00" },
      between: [
        { from: "虎跳峽", to: "獨克宗古城", km: 80, hours: "1:20" },
        { from: "獨克宗", to: "松贊林寺", km: 5, hours: "0:10" },
      ],
    },
    6: {
      dayStart: { from: "香格里拉", to: "普達措國家公園", km: 22, hours: "0:30" },
      between: [
        { from: "普達措", to: "屬都湖", km: 10, hours: "0:15" },
        { from: "屬都湖", to: "高山草甸", km: 15, hours: "0:20" },
      ],
    },
    7: {
      dayStart: { from: "香格里拉", to: "梅里雪山觀景點", km: 180, hours: "3:00" },
      between: [
        { from: "梅里雪山", to: "返港出發點", km: 1800, hours: "20" },
      ],
    },
  },

  // ─── 6. 內蒙古草原 ─────────────────────────────────────────────
  6: {
    1: {
      between: [
        { from: "香港", to: "海拉爾", km: 3200, hours: "35" },
        { from: "海拉爾市區", to: "成吉思汗廣場", km: 5, hours: "0:15" },
        { from: "廣場", to: "草原營地", km: 30, hours: "0:35" },
      ],
    },
    2: {
      dayStart: { from: "草原營地", to: "騎馬出發點", isWalking: true, walkTime: "5分鐘" },
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    3: {
      dayStart: { from: "草原營地", to: "莫日格勒河", km: 60, hours: "0:50" },
      between: [
        { from: "九曲灣觀景台", to: "額爾古納河沿岸", km: 40, hours: "0:45" },
        { from: "額爾古納河", to: "室韋俄羅斯族村", km: 35, hours: "0:40" },
      ],
    },
    4: {
      dayStart: { from: "室韋", to: "額爾古納濕地", km: 50, hours: "0:45" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { from: "濕地", to: "根河市", km: 35, hours: "0:40" },
      ],
    },
    5: {
      dayStart: { from: "根河", to: "滿洲里套娃廣場", km: 220, hours: "2:30" },
      between: [
        { from: "套娃廣場", to: "呼倫湖觀鳥點", km: 30, hours: "0:35" },
        { from: "呼倫湖", to: "邊境風情區", km: 20, hours: "0:25" },
      ],
    },
    6: {
      dayStart: { from: "滿洲里", to: "海拉爾要塞遺址", km: 90, hours: "1:20" },
      between: [
        { isWalking: true, walkTime: "20分鐘" },
        { from: "海拉爾市區", to: "香港返程", km: 3200, hours: "35" },
      ],
    },
  },

  // ─── 7. 絲綢之路 ─────────────────────────────────────────────
  7: {
    1: {
      between: [
        { from: "香港", to: "西安", km: 1800, hours: "19" },
        { from: "西安", to: "城牆", km: 10, hours: "0:20" },
        { from: "西安", to: "天水出發", km: 280, hours: "3:00" },
      ],
    },
    2: {
      dayStart: { from: "天水", to: "麥積山石窟", km: 45, hours: "0:45" },
      between: [
        { from: "麥積山", to: "蘭州", km: 280, hours: "3:00" },
        { from: "蘭州市區", to: "黃河鐵橋", km: 5, hours: "0:15" },
      ],
    },
    3: {
      dayStart: { from: "蘭州", to: "白塔山", km: 3, hours: "0:10" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { from: "蘭州", to: "武威雷台漢墓", km: 200, hours: "2:30" },
      ],
    },
    4: {
      dayStart: { from: "武威", to: "馬蹄寺石窟", km: 120, hours: "1:30" },
      between: [
        { from: "馬蹄寺", to: "張掖七彩丹霞", km: 90, hours: "1:10" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    5: {
      dayStart: { from: "張掖", to: "大佛寺", km: 5, hours: "0:10" },
      between: [
        { from: "張掖", to: "酒泉公園", km: 130, hours: "1:30" },
        { from: "酒泉", to: "嘉峪關長城", km: 20, hours: "0:25" },
      ],
    },
    6: {
      dayStart: { from: "嘉峪關", to: "玉門關遺址", km: 90, hours: "1:00" },
      between: [
        { from: "玉門關", to: "漢長城烽燧", km: 30, hours: "0:30" },
        { from: "烽燧", to: "敦煌市區", km: 100, hours: "1:10" },
      ],
    },
    7: {
      dayStart: { from: "敦煌酒店", to: "莫高窟", km: 25, hours: "0:30" },
      between: [
        { from: "莫高窟", to: "敦煌博物館", km: 25, hours: "0:30" },
        { from: "博物館", to: "沙州夜市", km: 3, hours: "0:10" },
      ],
    },
    8: {
      dayStart: { from: "敦煌酒店", to: "鳴沙山入口", km: 5, hours: "0:15" },
      between: [
        { from: "鳴沙山", to: "月牙泉", isWalking: true, walkTime: "15分鐘" },
        { from: "月牙泉", to: "敦煌夜市", km: 5, hours: "0:15" },
      ],
    },
    9: {
      between: [
        { from: "敦煌", to: "香港返程", km: 3500, hours: "38" },
      ],
    },
  },

  // ─── 8. 桂林山水 ─────────────────────────────────────────────
  8: {
    1: {
      between: [
        { from: "香港", to: "桂林", km: 650, hours: "7:00" },
        { from: "桂林市區", to: "象鼻山", km: 8, hours: "0:20" },
        { from: "象鼻山", to: "兩江四湖", km: 3, hours: "0:10" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "桂林", to: "漓江碼頭", km: 5, hours: "0:15" },
      between: [
        { from: "漓江上游", to: "陽朔（漓江終點）", km: 83, hours: "4:00" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    3: {
      dayStart: { from: "陽朔酒店", to: "十里畫廊入口", km: 3, hours: "0:10" },
      between: [
        { from: "十里畫廊", to: "月亮山", km: 8, hours: "0:15" },
        { from: "月亮山", to: "遇龍河竹筏碼頭", km: 12, hours: "0:20" },
      ],
    },
    4: {
      dayStart: { from: "陽朔", to: "龍脊梯田", km: 100, hours: "2:00" },
      between: [
        { from: "梯田觀景台", to: "瑤族村寨", isWalking: true, walkTime: "20分鐘" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    5: {
      dayStart: { from: "桂林酒店", to: "蘆笛岩", km: 8, hours: "0:15" },
      between: [
        { from: "蘆笛岩", to: "七星公園", km: 10, hours: "0:20" },
        { from: "桂林", to: "香港返程", km: 650, hours: "7:00" },
      ],
    },
  },

  // ─── 9. 張家界 ─────────────────────────────────────────────
  9: {
    1: {
      between: [
        { from: "香港", to: "張家界", km: 1100, hours: "12" },
        { from: "張家界", to: "土家族風情街", km: 5, hours: "0:15" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "張家界", to: "袁家界索道站", km: 35, hours: "0:45" },
      between: [
        { from: "袁家界", to: "天子山", km: 15, hours: "0:30" },
        { from: "天子山", to: "十里畫廊", km: 10, hours: "0:20" },
      ],
    },
    3: {
      dayStart: { from: "武陵源酒店", to: "天門山索道站", km: 30, hours: "0:40" },
      between: [
        { from: "索道頂站", to: "天門洞", isWalking: true, walkTime: "20分鐘" },
        { from: "天門洞", to: "玻璃棧道", isWalking: true, walkTime: "15分鐘" },
      ],
    },
    4: {
      dayStart: { from: "張家界", to: "黃龍洞", km: 30, hours: "0:35" },
      between: [
        { from: "黃龍洞", to: "寶峰湖", km: 5, hours: "0:10" },
        { from: "寶峰湖", to: "土家族文化演出場", km: 8, hours: "0:15" },
      ],
    },
    5: {
      between: [
        { from: "張家界", to: "香港返程", km: 1100, hours: "12" },
      ],
    },
  },

  // ─── 10. 東北雪鄉 ─────────────────────────────────────────────
  10: {
    1: {
      between: [
        { from: "香港", to: "哈爾濱", km: 3500, hours: "38" },
        { from: "哈爾濱市區", to: "中央大街", km: 8, hours: "0:20" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    2: {
      dayStart: { from: "哈爾濱酒店", to: "冰雪大世界", km: 10, hours: "0:20" },
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    3: {
      dayStart: { from: "哈爾濱", to: "雪鄉", km: 280, hours: "4:00" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    4: {
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { from: "雪鄉", to: "牡丹江市區", km: 180, hours: "2:30" },
      ],
    },
    5: {
      dayStart: { from: "牡丹江", to: "亞布力滑雪場", km: 130, hours: "1:45" },
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { from: "滑雪場", to: "溫泉區", km: 3, hours: "0:10" },
      ],
    },
    6: {
      dayStart: { from: "亞布力", to: "哈爾濱索菲亞教堂", km: 130, hours: "1:45" },
      between: [
        { from: "索菲亞教堂", to: "道外巴洛克建築", km: 3, hours: "0:15" },
        { from: "哈爾濱", to: "香港返程", km: 3500, hours: "38" },
      ],
    },
  },

  // ─── 11. 福建土樓 ─────────────────────────────────────────────
  11: {
    1: {
      between: [
        { from: "香港", to: "廈門", km: 450, hours: "5:00" },
        { from: "廈門", to: "土樓民宿（永定）", km: 150, hours: "2:00" },
        { isWalking: true, walkTime: "15分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    2: {
      dayStart: { from: "民宿", to: "振成樓", isWalking: true, walkTime: "20分鐘" },
      between: [
        { from: "振成樓", to: "承啟樓", km: 3, hours: "0:10" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    3: {
      dayStart: { from: "永定", to: "南靖田螺坑土樓群", km: 80, hours: "1:30" },
      between: [
        { from: "南靖土樓", to: "安溪茶園", km: 130, hours: "2:00" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    4: {
      dayStart: { from: "廈門", to: "曉起古村", km: 120, hours: "2:00" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { from: "曉起", to: "香港返程", km: 520, hours: "5:30" },
      ],
    },
  },

  // ─── 12. 西藏珠峰 ─────────────────────────────────────────────
  12: {
    1: {
      dayStart: { from: "拉薩酒店", to: "大昭寺廣場", km: 3, hours: "0:15" },
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "拉薩酒店", to: "布達拉宮", km: 3, hours: "0:15" },
      between: [
        { from: "布達拉宮", to: "羅布林卡", km: 2, hours: "0:10" },
        { from: "羅布林卡", to: "八廓街", km: 3, hours: "0:10" },
      ],
    },
    3: {
      dayStart: { from: "拉薩", to: "羊卓雍措", km: 100, hours: "2:00" },
      between: [
        { from: "羊卓雍措", to: "卡若拉冰川", km: 60, hours: "1:00" },
        { from: "卡若拉冰川", to: "日喀則", km: 90, hours: "1:30" },
      ],
    },
    4: {
      dayStart: { from: "日喀則酒店", to: "扎什倫布寺", km: 3, hours: "0:10" },
      between: [
        { from: "扎什倫布寺", to: "日喀則市區", isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    5: {
      dayStart: { from: "日喀則", to: "薩迦寺", km: 140, hours: "2:30" },
      between: [
        { from: "薩迦", to: "定日縣", km: 150, hours: "2:30" },
      ],
    },
    6: {
      dayStart: { from: "定日", to: "珠峰大本營（5200m）", km: 100, hours: "2:30" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    7: {
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { from: "珠峰大本營", to: "定日", km: 100, hours: "2:30" },
      ],
    },
    8: {
      dayStart: { from: "定日", to: "吉隆溝入口", km: 120, hours: "2:30" },
      between: [
        { isWalking: true, walkTime: "20分鐘" },
        { from: "吉隆溝", to: "吉隆鎮", km: 20, hours: "0:30" },
      ],
    },
    9: {
      dayStart: { from: "吉隆", to: "日喀則", km: 340, hours: "5:30" },
      between: [
        { from: "吉隆峽谷觀景點", to: "日喀則", km: 320, hours: "5:00" },
      ],
    },
    10: {
      dayStart: { from: "日喀則", to: "雅魯藏布江觀景點", km: 60, hours: "1:00" },
      between: [
        { from: "雅魯藏布江", to: "拉薩", km: 200, hours: "3:00" },
      ],
    },
    11: {
      dayStart: { from: "拉薩酒店", to: "色拉寺", km: 5, hours: "0:15" },
      between: [
        { from: "色拉寺", to: "甜茶館", km: 3, hours: "0:10" },
        { from: "甜茶館", to: "紀念品街", isWalking: true, walkTime: "10分鐘" },
      ],
    },
    12: {
      between: [],
    },
  },

  // ─── 13. 九寨溝 ─────────────────────────────────────────────
  13: {
    1: {
      between: [
        { from: "香港", to: "九寨溝", km: 2200, hours: "24" },
      ],
    },
    2: {
      dayStart: { from: "酒店", to: "九寨溝景區入口", km: 5, hours: "0:15" },
      between: [
        { isWalking: true, walkTime: "15分鐘" },
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    3: {
      dayStart: { from: "酒店", to: "景區入口", km: 5, hours: "0:15" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    4: {
      dayStart: { from: "酒店", to: "長海入口", km: 8, hours: "0:20" },
      between: [
        { isWalking: true, walkTime: "20分鐘" },
        { from: "長海", to: "藏族文化村", km: 15, hours: "0:20" },
      ],
    },
    5: {
      dayStart: { from: "九寨溝", to: "成都錦里古街", km: 450, hours: "5:30" },
      between: [
        { from: "九寨溝景區", to: "成都", km: 450, hours: "5:30" },
        { isWalking: true, walkTime: "20分鐘" },
      ],
    },
  },

  // ─── 14. 新疆喀納斯 ─────────────────────────────────────────────
  14: {
    1: {
      between: [
        { from: "香港", to: "布爾津", km: 3900, hours: "43" },
        { from: "布爾津市區", to: "額爾齊斯河夜市", km: 3, hours: "0:10" },
      ],
    },
    2: {
      dayStart: { from: "布爾津", to: "五彩灘", km: 24, hours: "0:30" },
      between: [
        { from: "五彩灘", to: "喀納斯景區入口", km: 200, hours: "3:00" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    3: {
      dayStart: { from: "喀納斯民宿", to: "神仙灣", km: 5, hours: "0:15" },
      between: [
        { from: "神仙灣", to: "月亮灣", km: 3, hours: "0:10" },
        { from: "月亮灣", to: "臥龍灣", km: 5, hours: "0:15" },
        { from: "臥龍灣", to: "觀魚台", km: 3, hours: "0:10" },
      ],
    },
    4: {
      dayStart: { from: "喀納斯", to: "禾木村", km: 35, hours: "1:00" },
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    5: {
      dayStart: { from: "禾木村", to: "白哈巴村", km: 30, hours: "0:50" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    6: {
      dayStart: { from: "白哈巴", to: "額爾齊斯河石林", km: 50, hours: "1:00" },
      between: [
        { from: "石林", to: "布爾津市區", km: 50, hours: "0:50" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    7: {
      dayStart: { from: "布爾津", to: "烏魯木齊", km: 500, hours: "5:30" },
      between: [
        { from: "布爾津", to: "烏魯木齊", km: 500, hours: "5:30" },
        { from: "烏魯木齊", to: "香港返程", km: 3900, hours: "43" },
      ],
    },
  },

  // ─── 15. 張掖丹霞 ─────────────────────────────────────────────
  15: {
    1: {
      between: [
        { from: "香港", to: "蘭州", km: 2100, hours: "22" },
        { from: "蘭州市區", to: "張掖", km: 400, hours: "4:00" },
        { from: "張掖市區", to: "大佛寺", km: 3, hours: "0:10" },
      ],
    },
    2: {
      dayStart: { from: "張掖酒店", to: "七彩丹霞晨光觀景台", km: 40, hours: "0:40" },
      between: [
        { from: "七彩丹霞", to: "冰溝丹霞", km: 20, hours: "0:25" },
        { from: "冰溝丹霞", to: "丹霞日落台", km: 3, hours: "0:10" },
      ],
    },
    3: {
      dayStart: { from: "張掖", to: "馬蹄寺石窟", km: 65, hours: "1:00" },
      between: [
        { from: "馬蹄寺", to: "山丹軍馬場", km: 110, hours: "1:30" },
        { from: "山丹", to: "祁連山雪景觀景點", km: 40, hours: "0:45" },
      ],
    },
    4: {
      dayStart: { from: "酒泉", to: "嘉峪關長城", km: 20, hours: "0:25" },
      between: [
        { from: "嘉峪關", to: "懸壁長城", km: 8, hours: "0:15" },
        { from: "懸壁長城", to: "黑山岩畫", km: 12, hours: "0:20" },
      ],
    },
    5: {
      dayStart: { from: "嘉峪關", to: "敦煌", km: 360, hours: "3:30" },
      between: [
        { from: "敦煌", to: "鳴沙山騎駱駝入口", km: 5, hours: "0:15" },
        { from: "鳴沙山", to: "月牙泉", isWalking: true, walkTime: "15分鐘" },
      ],
    },
  },

  // ─── 16. 貴州黔東南 ─────────────────────────────────────────────
  16: {
    1: {
      between: [
        { from: "香港", to: "貴陽", km: 1200, hours: "13" },
        { from: "貴陽", to: "鎮遠古城", km: 230, hours: "2:30" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "鎮遠", to: "舞陽河竹筏碼頭", km: 10, hours: "0:15" },
      between: [
        { from: "舞陽河", to: "西江千戶苗寨", km: 120, hours: "2:00" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    3: {
      dayStart: { from: "苗寨民宿", to: "長桌宴廣場", isWalking: true, walkTime: "10分鐘" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { from: "苗寨", to: "觀景台", isWalking: true, walkTime: "20分鐘" },
      ],
    },
    4: {
      dayStart: { from: "西江", to: "加榜梯田", km: 90, hours: "1:30" },
      between: [
        { from: "梯田觀景台", to: "苗族村寨", km: 5, hours: "0:15" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    5: {
      dayStart: { from: "加榜", to: "肇興侗寨", km: 80, hours: "1:30" },
      between: [
        { isWalking: true, walkTime: "15分鐘" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    6: {
      dayStart: { from: "肇興侗寨", to: "黎平翹街", km: 60, hours: "1:00" },
      between: [
        { from: "黎平", to: "貴陽", km: 300, hours: "4:00" },
        { from: "貴陽", to: "香港返程", km: 1200, hours: "13" },
      ],
    },
  },

  // ─── 17. 川西秘境 ─────────────────────────────────────────────
  17: {
    1: {
      between: [
        { from: "香港", to: "成都/雅安", km: 2100, hours: "22" },
        { from: "雅安市區", to: "蒼山洱海觀景點", km: 5, hours: "0:20" },
        { from: "雅安", to: "民宿", km: 8, hours: "0:20" },
      ],
    },
    2: {
      dayStart: { from: "雅安", to: "折多山隧道", km: 150, hours: "2:30" },
      between: [
        { from: "折多山", to: "康定市區", km: 35, hours: "0:40" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    3: {
      dayStart: { from: "康定", to: "高爾寺山", km: 100, hours: "2:00" },
      between: [
        { from: "高爾寺山", to: "理塘世界高城廣場", km: 60, hours: "1:00" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    4: {
      dayStart: { from: "理塘", to: "鹽井鹽田", km: 280, hours: "4:00" },
      between: [
        { from: "鹽井", to: "如美溫泉", km: 15, hours: "0:20" },
        { from: "如美", to: "芒康", km: 30, hours: "0:30" },
      ],
    },
    5: {
      dayStart: { from: "芒康", to: "業拉山72拐", km: 120, hours: "3:00" },
      between: [
        { from: "業拉山", to: "怒江大峽谷觀景點", km: 40, hours: "0:45" },
        { from: "怒江峽谷", to: "左貢", km: 50, hours: "1:00" },
      ],
    },
    6: {
      dayStart: { from: "左貢", to: "來古冰川", km: 210, hours: "4:30" },
      between: [
        { from: "來古冰川", to: "然烏湖", km: 5, hours: "0:15" },
        { isWalking: true, walkTime: "20分鐘" },
      ],
    },
    7: {
      dayStart: { from: "然烏湖", to: "林芝魯朗林海", km: 130, hours: "2:30" },
      between: [
        { from: "魯朗", to: "扎西崗村", km: 5, hours: "0:10" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    8: {
      dayStart: { from: "林芝", to: "米拉山口（5013m）", km: 170, hours: "3:30" },
      between: [
        { from: "米拉山口", to: "拉薩", km: 100, hours: "2:00" },
        { isWalking: true, walkTime: "20分鐘" },
      ],
    },
    9: {
      dayStart: { from: "拉薩", to: "布達拉宮", km: 3, hours: "0:15" },
      between: [
        { from: "布達拉宮", to: "大昭寺", isWalking: true, walkTime: "5分鐘" },
        { from: "拉薩", to: "香港返程", km: 4200, hours: "46" },
      ],
    },
  },

  // ─── 18. 甘南藏區 ─────────────────────────────────────────────
  18: {
    1: {
      between: [
        { from: "香港", to: "蘭州", km: 2100, hours: "22" },
        { from: "蘭州", to: "夏河/拉卜楞寺", km: 260, hours: "3:30" },
        { isWalking: true, walkTime: "15分鐘" },
      ],
    },
    2: {
      dayStart: { from: "夏河民宿", to: "清晨誦經現場", isWalking: true, walkTime: "5分鐘" },
      between: [
        { isWalking: true, walkTime: "5分鐘" },
        { from: "拉卜楞寺", to: "甘加秘境草原", km: 50, hours: "0:50" },
      ],
    },
    3: {
      dayStart: { from: "夏河", to: "扎尕那石城", km: 130, hours: "2:00" },
      between: [
        { isWalking: true, walkTime: "10分鐘" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    4: {
      dayStart: { from: "扎尕那", to: "郎木寺峽谷", km: 60, hours: "1:00" },
      between: [
        { from: "郎木寺峽谷", to: "白龍江源頭", isWalking: true, walkTime: "15分鐘" },
        { from: "白龍江", to: "納摩峽谷", km: 5, hours: "0:15" },
      ],
    },
    5: {
      dayStart: { from: "郎木寺", to: "若爾蓋大草原", km: 80, hours: "1:10" },
      between: [
        { from: "若爾蓋草原", to: "黃河第一彎", km: 30, hours: "0:35" },
        { from: "黃河第一彎", to: "花湖", km: 20, hours: "0:25" },
      ],
    },
    6: {
      dayStart: { from: "若爾蓋", to: "則岔石林", km: 60, hours: "0:55" },
      between: [
        { from: "則岔石林", to: "尕海湖", km: 40, hours: "0:45" },
        { from: "尕海湖", to: "郎木寺", km: 50, hours: "0:55" },
      ],
    },
    7: {
      dayStart: { from: "郎木寺", to: "臨夏回族文化區", km: 180, hours: "2:30" },
      between: [
        { from: "臨夏", to: "蘭州", km: 130, hours: "1:30" },
        { from: "蘭州", to: "香港返程", km: 2100, hours: "22" },
      ],
    },
  },

  // ─── 19. 恩施大峽谷 ─────────────────────────────────────────────
  19: {
    1: {
      between: [
        { from: "香港", to: "武漢", km: 1000, hours: "10:30" },
        { from: "武漢", to: "恩施", km: 390, hours: "4:00" },
        { isWalking: true, walkTime: "5分鐘" },
      ],
    },
    2: {
      dayStart: { from: "恩施", to: "大峽谷入口（七星寨）", km: 30, hours: "0:40" },
      between: [
        { from: "七星寨", to: "雲龍地縫", km: 5, hours: "0:15" },
        { isWalking: true, walkTime: "30分鐘" },
      ],
    },
    3: {
      dayStart: { from: "恩施酒店", to: "騰龍洞入口", km: 25, hours: "0:30" },
      between: [
        { from: "騰龍洞", to: "清江畫廊碼頭", km: 20, hours: "0:25" },
        { from: "清江畫廊", to: "土家風雨橋", km: 10, hours: "0:20" },
      ],
    },
    4: {
      dayStart: { from: "清江民宿", to: "彭家寨", km: 40, hours: "0:45" },
      between: [
        { from: "彭家寨", to: "刺繡體驗坊", isWalking: true, walkTime: "10分鐘" },
        { from: "彭家寨", to: "利川龍船水鄉", km: 80, hours: "1:10" },
      ],
    },
    5: {
      dayStart: { from: "恩施", to: "土特產市場", km: 5, hours: "0:15" },
      between: [
        { from: "恩施", to: "武漢", km: 390, hours: "4:00" },
        { from: "武漢", to: "香港返程", km: 1000, hours: "10:30" },
      ],
    },
  },

  // ─── 20. 婺源 ─────────────────────────────────────────────
  20: {
    1: {
      between: [
        { from: "香港", to: "南昌", km: 900, hours: "9:30" },
        { from: "南昌", to: "婺源", km: 250, hours: "2:30" },
        { isWalking: true, walkTime: "10分鐘" },
      ],
    },
    2: {
      dayStart: { from: "婺源古村民宿", to: "篁嶺景區", km: 8, hours: "0:20" },
      between: [
        { from: "篁嶺", to: "江嶺梯田", km: 15, hours: "0:25" },
        { from: "婺源", to: "香港返程", km: 1150, hours: "12" },
      ],
    },
  },
};
